import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { normalizeExternalNetworkGraphViewMode } from '../utils/external-network-graph.utils';
import { createTenantStorage } from './tenantStorage';

export type SurfaceGraphViewMode = 'full';

type SurfaceGraphViewModeState = {
  viewModeBySurfaceId: Record<string, SurfaceGraphViewMode>;
  setViewMode: ({
    surfaceId,
    viewMode,
  }: {
    surfaceId: string;
    viewMode: SurfaceGraphViewMode;
  }) => void;
};

type PersistedSurfaceGraphViewModeState = Pick<
  SurfaceGraphViewModeState,
  'viewModeBySurfaceId'
>;

function parseViewModeBySurfaceId(
  value: unknown,
): Record<string, SurfaceGraphViewMode> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  const parsed: Record<string, SurfaceGraphViewMode> = {};
  for (const [surfaceId, viewMode] of Object.entries(value)) {
    if (viewMode !== 'full' && viewMode !== 'risk' && viewMode !== 'exposed') {
      return null;
    }
    parsed[surfaceId] = normalizeExternalNetworkGraphViewMode(viewMode);
  }

  return parsed;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parsePersistedSurfaceGraphViewModeState(
  value: unknown,
): PersistedSurfaceGraphViewModeState | null {
  if (!isRecord(value)) {
    return null;
  }

  const viewModeBySurfaceId = parseViewModeBySurfaceId(
    value.viewModeBySurfaceId,
  );
  if (!viewModeBySurfaceId) {
    return null;
  }

  return { viewModeBySurfaceId };
}

function migratePersistedSurfaceGraphViewModeState(
  persistedState: unknown,
): PersistedSurfaceGraphViewModeState {
  const current = parsePersistedSurfaceGraphViewModeState(persistedState);
  if (current) {
    return current;
  }

  return { viewModeBySurfaceId: {} };
}

export const useExternalNetworkGraphViewModeStore =
  create<SurfaceGraphViewModeState>()(
    persist(
      (set) => ({
        viewModeBySurfaceId: {},
        setViewMode: ({ surfaceId, viewMode }) =>
          set((state) => ({
            viewModeBySurfaceId: {
              ...state.viewModeBySurfaceId,
              [surfaceId]: viewMode,
            },
          })),
      }),
      {
        name: 'terra-proxy-external-network-graph-view-mode',
        version: 2,
        storage: createJSONStorage(() => createTenantStorage()),
        migrate: migratePersistedSurfaceGraphViewModeState,
        partialize: (state): PersistedSurfaceGraphViewModeState => ({
          viewModeBySurfaceId: state.viewModeBySurfaceId,
        }),
      },
    ),
  );
